import {
  PROJECTION_METHOD_LABELS,
  plannedOptionsSchema,
  plannedReportSchema,
  targetPeriodLabel,
  TRAFFIC_LIGHT_LABELS,
  type PlannedOptions,
  type PlannedReport,
  type PlannedRow,
  type TrafficLight,
} from '@fluxus/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { FolderKanban, LineChart as LineChartIcon } from 'lucide-react';
import { useId, useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { StatusBadge } from '@/components/status/StatusBadge';
import { Button } from '@/components/ui/button';
import { NativeSelect } from '@/components/ui/select';
import { ProjectPicker, useCurrentProject } from '@/features/workspace/workspace';
import { useAuth, useCurrentUser } from '@/lib/auth/session';
import { cn } from '@/lib/cn';

const formatValue = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
const withUnit = (v: number | null, unit: string) =>
  v === null ? '—' : unit === 'R$' ? `R$ ${formatValue(v)}` : `${formatValue(v)} ${unit}`;
const signed = (v: number | null, unit: string) =>
  v === null ? '—' : `${v > 0 ? '+' : v < 0 ? '−' : ''}${withUnit(Math.abs(v), unit)}`;
const signedPercent = (v: number | null) =>
  v === null ? '—' : `${v > 0 ? '+' : v < 0 ? '−' : ''}${formatValue(Math.abs(v))}%`;
const percent = (v: number | null) => (v === null ? null : `${formatValue(v)}%`);
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

/**
 * Indicadores › Previsto x Realizado (T-068, RF-26, RF-30, PRD §11.14; DEC-038): mês a mês e acumulado,
 * diferenças, % consumido e saldo, projeção de fechamento e a meta original, com filtros.
 */
export function PrevistoRealizadoPage() {
  const { organizationId = '' } = useParams();
  const user = useCurrentUser();
  const { workspace, projects, project, select } = useCurrentProject(organizationId);
  return (
    <PageContainer className="max-w-[90rem]">
      <PageHeader
        title="Previsto x Realizado"
        description="Compare a meta com o resultado mês a mês e no acumulado do ano, com as diferenças, o quanto da meta já foi realizado e a projeção de fechamento."
      />
      {workspace.isPending ? (
        <LoadingState className="mt-6" rows={4} label="Carregando o projeto" />
      ) : workspace.isError ? (
        <ErrorState
          className="mt-6"
          error={workspace.error}
          onRetry={() => void workspace.refetch()}
          retrying={workspace.isFetching}
        />
      ) : !project ? (
        <EmptyState
          className="mt-6 rounded-lg border border-dashed border-border py-12"
          icon={FolderKanban}
          title="Nenhum projeto BSC em andamento"
          description="Metas e resultados pertencem a um projeto. Quando a empresa tiver um projeto em andamento, eles aparecem aqui."
          action={
            user.organization.isFluxus ? (
              <Button asChild variant="outline">
                <Link to={`/empresas/${organizationId}`}>Abrir um projeto na página da empresa</Link>
              </Button>
            ) : null
          }
        />
      ) : (
        <div key={project.id} className="mt-4 flex flex-col gap-4">
          <ProjectPicker projects={projects} project={project} onSelect={select} />
          <PlannedSection organizationId={organizationId} projectId={project.id} />
        </div>
      )}
    </PageContainer>
  );
}

function PlannedSection({ organizationId, projectId }: { organizationId: string; projectId: string }) {
  const { client } = useAuth();
  const base = `/organizations/${organizationId}/projects/${projectId}/planned-vs-actual`;
  const options = useQuery({
    queryKey: ['planned', projectId, 'options'],
    queryFn: ({ signal }) => client.request(`${base}/options`, { schema: plannedOptionsSchema, signal }),
  });
  if (options.isPending) return <LoadingState rows={4} label="Carregando os filtros" />;
  if (options.isError) {
    return (
      <ErrorState
        error={options.error}
        onRetry={() => void options.refetch()}
        retrying={options.isFetching}
      />
    );
  }
  if (!options.data.kpis.length) {
    return (
      <EmptyState
        className="rounded-lg border border-dashed border-border py-12"
        icon={LineChartIcon}
        title="Nenhum indicador para comparar"
        description="Os indicadores das suas áreas (ou lançados pelas suas unidades) aparecem aqui com o previsto e o realizado."
      />
    );
  }
  return <PlannedView base={base} projectId={projectId} options={options.data} />;
}

function PlannedView({
  base,
  projectId,
  options,
}: {
  base: string;
  projectId: string;
  options: PlannedOptions;
}) {
  const { client } = useAuth();
  const nowYear = new Date().getFullYear();
  const [year, setYear] = useState(options.years.includes(nowYear) ? nowYear : (options.years[0] ?? nowYear));
  const [from, setFrom] = useState(1);
  const [to, setTo] = useState(12);
  const [perspectiveId, setPerspectiveId] = useState('');
  const [objectiveId, setObjectiveId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [kpiId, setKpiId] = useState('');
  const [unitId, setUnitId] = useState('');
  const [compareOriginal, setCompareOriginal] = useState(false);
  const ids = {
    year: useId(),
    from: useId(),
    to: useId(),
    perspective: useId(),
    objective: useId(),
    department: useId(),
    kpi: useId(),
    unit: useId(),
  };

  const objectives = options.objectives.filter((o) => !perspectiveId || o.perspectiveId === perspectiveId);
  const kpis = options.kpis.filter(
    (k) =>
      (!perspectiveId || k.perspectiveId === perspectiveId) &&
      (!objectiveId || k.objectiveIds.includes(objectiveId)) &&
      (!departmentId || k.departmentId === departmentId),
  );
  const kpi = kpis.find((k) => k.id === kpiId) ?? kpis[0];
  // Pela área: o consolidado e todas as unidades. Pela unidade: só as próprias (a primeira, de início).
  const units = kpi?.access === 'UNIT' ? options.units.filter((u) => u.own) : options.units;
  const unit = units.some((u) => u.id === unitId)
    ? unitId
    : kpi?.access === 'UNIT'
      ? (units[0]?.id ?? '')
      : '';

  const report = useQuery({
    queryKey: ['planned', projectId, kpi?.id, year, unit],
    queryFn: ({ signal }) =>
      client.request(base, {
        query: { kpiId: kpi!.id, year: String(year), ...(unit ? { unitId: unit } : {}) },
        schema: plannedReportSchema,
        signal,
      }),
    enabled: Boolean(kpi),
    placeholderData: keepPreviousData,
  });

  const filter = (
    label: string,
    id: string,
    value: string,
    onChange: (v: string) => void,
    children: React.ReactNode,
  ) => (
    <div>
      <label htmlFor={id} className="mb-1 block text-xs font-medium text-muted-foreground">
        {label}
      </label>
      <NativeSelect id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        {children}
      </NativeSelect>
    </div>
  );

  return (
    <div className="flex flex-col gap-5">
      <section aria-label="Filtros" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {filter(
          'Ano',
          ids.year,
          String(year),
          (v) => setYear(Number(v)),
          options.years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          )),
        )}
        <div className="grid grid-cols-2 gap-2">
          {filter(
            'De',
            ids.from,
            String(from),
            (v) => {
              setFrom(Number(v));
              if (Number(v) > to) setTo(Number(v));
            },
            MONTHS.map((m) => (
              <option key={m} value={m}>
                {targetPeriodLabel('MONTH', m)}
              </option>
            )),
          )}
          {filter(
            'Até',
            ids.to,
            String(to),
            (v) => {
              setTo(Number(v));
              if (Number(v) < from) setFrom(Number(v));
            },
            MONTHS.map((m) => (
              <option key={m} value={m}>
                {targetPeriodLabel('MONTH', m)}
              </option>
            )),
          )}
        </div>
        {filter(
          'Perspectiva',
          ids.perspective,
          perspectiveId,
          (v) => {
            setPerspectiveId(v);
            setObjectiveId('');
            setKpiId('');
          },
          [
            <option key="" value="">
              Todas as perspectivas
            </option>,
            ...options.perspectives.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            )),
          ],
        )}
        {filter(
          'Objetivo',
          ids.objective,
          objectiveId,
          (v) => {
            setObjectiveId(v);
            setKpiId('');
          },
          [
            <option key="" value="">
              Todos os objetivos
            </option>,
            ...objectives.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            )),
          ],
        )}
        {filter(
          'Área',
          ids.department,
          departmentId,
          (v) => {
            setDepartmentId(v);
            setKpiId('');
          },
          [
            <option key="" value="">
              Todas as áreas
            </option>,
            ...options.departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            )),
          ],
        )}
        {filter(
          'Indicador',
          ids.kpi,
          kpi?.id ?? '',
          (v) => {
            setKpiId(v);
            setUnitId('');
          },
          kpis.length
            ? kpis.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.code} — {k.name}
                </option>
              ))
            : [
                <option key="" value="">
                  Nenhum indicador com estes filtros
                </option>,
              ],
        )}
        {filter('Unidade', ids.unit, unit, setUnitId, [
          ...(kpi?.access !== 'UNIT'
            ? [
                <option key="" value="">
                  Consolidado da empresa
                </option>,
              ]
            : []),
          ...units.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          )),
        ])}
        <label className="flex items-center gap-2 self-end pb-2 text-sm">
          <input
            type="checkbox"
            className="size-4 accent-(--brand)"
            checked={compareOriginal}
            onChange={(e) => setCompareOriginal(e.target.checked)}
          />
          Comparar com a meta original
        </label>
      </section>

      {!kpi ? (
        <EmptyState
          className="rounded-lg border border-dashed border-border py-12"
          icon={LineChartIcon}
          title="Nenhum indicador com estes filtros"
          description="Troque a perspectiva, o objetivo ou a área para ver outros indicadores."
        />
      ) : report.isPending ? (
        <LoadingState rows={4} label="Carregando o previsto x realizado" />
      ) : report.isError ? (
        <ErrorState error={report.error} onRetry={() => void report.refetch()} retrying={report.isFetching} />
      ) : (
        <ReportView
          data={report.data}
          from={from}
          to={to}
          compareOriginal={compareOriginal}
          busy={report.isFetching}
        />
      )}
    </div>
  );
}

function ReportView({
  data,
  from,
  to,
  compareOriginal,
  busy,
}: {
  data: PlannedReport;
  from: number;
  to: number;
  compareOriginal: boolean;
  busy: boolean;
}) {
  const [mode, setMode] = useState<'month' | 'ytd'>('month');
  const [difference, setDifference] = useState<'absolute' | 'percent'>('absolute');
  const unitLabel = data.kpi.unitLabel;
  const sum = data.kpi.aggregation === 'SUM';
  const rows = data.rows.filter((r) => r.month >= from && r.month <= to);
  const s = data.summary;
  const scope = data.unit ? data.unit.name : 'consolidado da empresa';
  const periodLabel = `${targetPeriodLabel('MONTH', from)} a ${targetPeriodLabel('MONTH', to)}/${data.year}`;

  return (
    <div className="flex flex-col gap-5" aria-busy={busy}>
      <section aria-label="Resumo do ano" className="rounded-lg border border-border bg-surface p-4">
        <h2 className="text-sm font-semibold">
          {data.kpi.code} — {data.kpi.name} · {scope} · {data.year}
        </h2>
        <dl className="mt-3 grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <Fact label="Meta do ano" value={withUnit(s.annualTarget, unitLabel)} />
          <Fact
            label={
              s.lastActualMonth
                ? `Realizado até ${targetPeriodLabel('MONTH', s.lastActualMonth)}`
                : 'Realizado'
            }
            value={withUnit(s.ytdActual, unitLabel)}
          />
          {s.consumedPercent !== null ? (
            <>
              <Fact label="% da meta do ano consumido" value={`${formatValue(s.consumedPercent)}%`} />
              <Fact label="Saldo até a meta do ano" value={withUnit(s.balance, unitLabel)} />
            </>
          ) : null}
          {s.projections.map((p) => (
            <Fact
              key={p.method}
              label={`Fechamento projetado (${PROJECTION_METHOD_LABELS[p.method].toLowerCase()})`}
              value={
                p.closing === null
                  ? 'Sem realizado suficiente'
                  : `${withUnit(p.closing, unitLabel)}${p.closingPerformance !== null ? ` · ${formatValue(p.closingPerformance)}% da meta` : ''}`
              }
            />
          ))}
        </dl>
        <p className="mt-3 text-xs text-muted-foreground">
          Tendência linear: reta pelos últimos até 6 períodos com realizado. Run-rate: a média do realizado
          repetida nos meses que faltam. O fechamento soma (ou faz a média, conforme o indicador) o realizado
          e o projetado.
          {s.consumedPercent === null ? ' % consumido e saldo valem para indicadores de soma.' : ''}
        </p>
      </section>

      <section
        aria-label="Gráfico do previsto x realizado"
        className="rounded-lg border border-border bg-surface p-4"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold">Previsto x realizado · {periodLabel}</h2>
          <Toggle
            label="Visão do gráfico"
            value={mode}
            onChange={setMode}
            options={[
              ['month', 'Mensal'],
              ['ytd', 'Acumulado'],
            ]}
          />
        </div>
        <ValuesChart rows={rows} mode={mode} unitLabel={unitLabel} compareOriginal={compareOriginal} />
        <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold">Diferença {mode === 'ytd' ? 'acumulada' : 'do mês'}</h2>
          <Toggle
            label="Diferença"
            value={difference}
            onChange={setDifference}
            options={[
              ['absolute', 'Absoluta'],
              ['percent', 'Percentual'],
            ]}
          />
        </div>
        <DifferenceChart
          rows={rows}
          mode={mode}
          kind={difference}
          unitLabel={unitLabel}
          direction={data.kpi.direction}
        />
      </section>

      <div
        role="region"
        aria-label={`Previsto x realizado de ${periodLabel}`}
        tabIndex={0}
        className="overflow-x-auto rounded-lg border border-border"
      >
        <table className="w-full min-w-[56rem] border-collapse text-sm">
          <caption className="sr-only">
            {data.kpi.name} — previsto x realizado de {periodLabel}
          </caption>
          <thead className="bg-muted/60 text-xs text-muted-foreground">
            <tr>
              <th scope="col" className="px-3 py-2 text-left font-semibold">
                Mês
              </th>
              <th scope="col" className="px-3 py-2 text-right font-semibold">
                Previsto
              </th>
              {compareOriginal ? (
                <th scope="col" className="px-3 py-2 text-right font-semibold">
                  Meta original
                </th>
              ) : null}
              <th scope="col" className="px-3 py-2 text-right font-semibold">
                Realizado
              </th>
              <th scope="col" className="px-3 py-2 text-right font-semibold">
                Diferença
              </th>
              <th scope="col" className="px-3 py-2 text-left font-semibold">
                Performance
              </th>
              {compareOriginal ? (
                <th scope="col" className="px-3 py-2 text-left font-semibold">
                  Contra a original
                </th>
              ) : null}
              <th scope="col" className="px-3 py-2 text-right font-semibold">
                Previsto acumulado
              </th>
              <th scope="col" className="px-3 py-2 text-right font-semibold">
                Realizado acumulado
              </th>
              <th scope="col" className="px-3 py-2 text-right font-semibold">
                Diferença acumulada
              </th>
              <th scope="col" className="px-3 py-2 text-left font-semibold">
                Performance acumulada
              </th>
              {sum ? (
                <>
                  <th scope="col" className="px-3 py-2 text-right font-semibold">
                    % consumido
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-semibold">
                    Saldo
                  </th>
                </>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.month} className="border-t border-border">
                <th scope="row" className="px-3 py-2 text-left font-semibold">
                  {targetPeriodLabel('MONTH', r.month)}
                </th>
                <td className="px-3 py-2 text-right tabular-nums">{withUnit(r.target, unitLabel)}</td>
                {compareOriginal ? (
                  <td
                    className={cn(
                      'px-3 py-2 text-right tabular-nums',
                      r.originalTarget !== r.target && 'font-semibold text-brand-strong dark:text-brand',
                    )}
                  >
                    {withUnit(r.originalTarget, unitLabel)}
                  </td>
                ) : null}
                <td className="px-3 py-2 text-right tabular-nums">
                  {r.actual !== null ? (
                    withUnit(r.actual, unitLabel)
                  ) : r.projectedRunRate !== null ? (
                    <span className="text-muted-foreground">
                      Projetado {withUnit(r.projectedRunRate, unitLabel)}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {signed(r.difference, unitLabel)}
                  {r.differencePercent !== null ? (
                    <span className="block text-xs text-muted-foreground">
                      {signedPercent(r.differencePercent)}
                    </span>
                  ) : null}
                </td>
                <td className="px-3 py-2">
                  <StatusBadge
                    status={r.light}
                    label={percent(r.performance) ?? TRAFFIC_LIGHT_LABELS[r.light]}
                  />
                </td>
                {compareOriginal ? (
                  <td className="px-3 py-2">
                    <StatusBadge
                      status={r.originalLight}
                      label={percent(r.originalPerformance) ?? TRAFFIC_LIGHT_LABELS[r.originalLight]}
                    />
                  </td>
                ) : null}
                <td className="px-3 py-2 text-right tabular-nums">{withUnit(r.ytdTarget, unitLabel)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{withUnit(r.ytdActual, unitLabel)}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {r.actual === null ? '—' : signed(r.ytdDifference, unitLabel)}
                  {r.actual !== null && r.ytdDifferencePercent !== null ? (
                    <span className="block text-xs text-muted-foreground">
                      {signedPercent(r.ytdDifferencePercent)}
                    </span>
                  ) : null}
                </td>
                <td className="px-3 py-2">
                  <StatusBadge
                    status={r.ytdLight}
                    label={percent(r.ytdPerformance) ?? TRAFFIC_LIGHT_LABELS[r.ytdLight]}
                  />
                </td>
                {sum ? (
                  <>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.ytdConsumedPercent === null ? '—' : `${formatValue(r.ytdConsumedPercent)}%`}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{withUnit(r.ytdBalance, unitLabel)}</td>
                  </>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {compareOriginal && !data.hasRevisions ? (
        <p className="text-sm text-muted-foreground">
          Nenhuma meta deste ano foi revisada: a original é a vigente.
        </p>
      ) : null}
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 font-semibold tabular-nums">{value}</dd>
    </div>
  );
}

function Toggle<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: [T, string][];
}) {
  return (
    <div role="group" aria-label={label} className="inline-flex rounded-md border border-border p-0.5">
      {options.map(([v, text]) => (
        <button
          key={v}
          type="button"
          aria-pressed={value === v}
          onClick={() => onChange(v)}
          className={cn(
            'rounded px-3 py-1 text-xs font-medium',
            value === v
              ? 'bg-brand-soft text-brand-strong dark:text-brand'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {text}
        </button>
      ))}
    </div>
  );
}

/**
 * Pontos do gráfico de valores: previsto, realizado, original e as projeções. No acumulado, a projeção vem
 * pronta da API (realizado + projetado pela agregação do KPI: soma, média ou último valor — RN-27).
 */
function chartPoints(rows: PlannedRow[], mode: 'month' | 'ytd') {
  return rows.map((r) => ({
    name: targetPeriodLabel('MONTH', r.month),
    previsto: mode === 'month' ? r.target : r.ytdTarget,
    realizado: mode === 'month' ? r.actual : r.actual === null ? null : r.ytdActual,
    original: mode === 'month' ? r.originalTarget : null,
    linear: mode === 'month' ? r.projectedLinear : r.ytdProjectedLinear,
    runRate: mode === 'month' ? r.projectedRunRate : r.ytdProjectedRunRate,
  }));
}

function ValuesChart({
  rows,
  mode,
  unitLabel,
  compareOriginal,
}: {
  rows: PlannedRow[];
  mode: 'month' | 'ytd';
  unitLabel: string;
  compareOriginal: boolean;
}) {
  const points = chartPoints(rows, mode);
  const summary = points
    .map(
      (p) =>
        `${p.name}: previsto ${withUnit(p.previsto, unitLabel)}, realizado ${
          p.realizado !== null
            ? withUnit(p.realizado, unitLabel)
            : p.runRate !== null
              ? `projetado ${withUnit(p.runRate, unitLabel)}`
              : 'sem dados'
        }`,
    )
    .join('; ');
  const tick = { fontSize: 12, fill: 'var(--muted-foreground)' };
  return (
    <div
      className="mt-3 h-72 w-full"
      role="img"
      aria-label={`Gráfico ${mode === 'ytd' ? 'acumulado' : 'mensal'} — ${summary}.`}
    >
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={points} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
          <XAxis dataKey="name" tick={tick} />
          <YAxis tick={tick} width={64} tickFormatter={(v: number) => formatValue(v)} />
          <Tooltip formatter={(v) => (typeof v === 'number' ? withUnit(v, unitLabel) : '—')} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Line
            type="monotone"
            dataKey="previsto"
            name="Previsto"
            stroke="var(--muted-foreground)"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="realizado"
            name="Realizado"
            stroke="var(--brand)"
            strokeWidth={2.5}
            isAnimationActive={false}
            connectNulls={false}
          />
          {compareOriginal && mode === 'month' ? (
            <Line
              type="monotone"
              dataKey="original"
              name="Meta original"
              stroke="var(--status-yellow-solid)"
              strokeDasharray="5 4"
              dot={false}
              isAnimationActive={false}
            />
          ) : null}
          <Line
            type="monotone"
            dataKey="linear"
            name="Projeção (tendência)"
            stroke="var(--brand)"
            strokeDasharray="4 4"
            dot={false}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="runRate"
            name="Projeção (run-rate)"
            stroke="var(--status-blue-solid)"
            strokeDasharray="2 4"
            dot={false}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function DifferenceChart({
  rows,
  mode,
  kind,
  unitLabel,
  direction,
}: {
  rows: PlannedRow[];
  mode: 'month' | 'ytd';
  kind: 'absolute' | 'percent';
  unitLabel: string;
  /** Direção do KPI: o que é favorável depende dela (gastar menos é bom no "menor, melhor"). */
  direction: PlannedReport['kpi']['direction'];
}) {
  const points = rows.map((r) => {
    const value =
      kind === 'absolute'
        ? mode === 'month'
          ? r.difference
          : r.actual === null
            ? null
            : r.ytdDifference
        : mode === 'month'
          ? r.differencePercent
          : r.actual === null
            ? null
            : r.ytdDifferencePercent;
    // Favorável pelo sinal nos KPIs de "maior/menor, melhor"; na faixa ideal e na fórmula, pelo semáforo.
    const light = mode === 'month' ? r.light : r.ytdLight;
    const tone: TrafficLight | null =
      value === null
        ? null
        : direction === 'HIGHER_BETTER'
          ? value >= 0
            ? 'green'
            : 'red'
          : direction === 'LOWER_BETTER'
            ? value <= 0
              ? 'green'
              : 'red'
            : light;
    return { name: targetPeriodLabel('MONTH', r.month), value, tone };
  });
  const label = (v: number | null) => (kind === 'absolute' ? signed(v, unitLabel) : signedPercent(v));
  const toneLabel = (tone: TrafficLight | null) =>
    tone === 'green'
      ? ' (favorável)'
      : tone === 'red'
        ? ' (desfavorável)'
        : tone
          ? ` (${TRAFFIC_LIGHT_LABELS[tone].toLowerCase()})`
          : '';
  const tick = { fontSize: 12, fill: 'var(--muted-foreground)' };
  return (
    <div
      className="mt-3 h-48 w-full"
      role="img"
      aria-label={`Diferença ${kind === 'absolute' ? 'absoluta' : 'percentual'}: ${points
        .map((p) => `${p.name} ${p.value === null ? 'sem dados' : `${label(p.value)}${toneLabel(p.tone)}`}`)
        .join('; ')}.`}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={points} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
          <XAxis dataKey="name" tick={tick} />
          <YAxis
            tick={tick}
            width={64}
            tickFormatter={(v: number) => (kind === 'percent' ? `${formatValue(v)}%` : formatValue(v))}
          />
          <ReferenceLine y={0} stroke="var(--border-strong)" />
          <Bar dataKey="value" name="Diferença" isAnimationActive={false}>
            {points.map((p) => (
              <Cell key={p.name} fill={`var(--status-${p.tone ?? 'gray'}-solid)`} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
